import { useQuery } from '@apollo/client';

import { TOTAL_NUMBER_OF_SKILLS_TO_DISPLAY } from 'bundles/onboarding-common/constants';
import { QUESTION_IDS } from 'bundles/onboarding-common/constants/questionScheme';
import { getPopularSkills } from 'bundles/onboarding-common/data/constants';
import useFetchData from 'bundles/onboarding-common/hooks/useFetchData';
import useOccupationPreferencesData from 'bundles/onboarding-common/hooks/useOccupationPreferencesData';
import GetSkillsFromRankedRoles from 'bundles/onboarding-common/queries/GetSkillsFromRankedRoles.graphql';
import type {
  GetSkillsFromRankedRolesQuery,
  GetSkillsFromRankedRolesQueryVariables,
} from 'bundles/onboarding-common/queries/__generated__/GetSkillsFromRankedRoles';

export type Skill = {
  id: string;
  skillName: string;
};

type SkillType = 'POPULAR_SKILLS' | 'ROLE_SKILLS';

type SkillsData = {
  allSkills: Skill[];
  loading: boolean;
  type: SkillType;
};

const useGetAllSkills = ({
  skip,
  limit = TOTAL_NUMBER_OF_SKILLS_TO_DISPLAY,
}: { skip?: boolean; limit?: number } = {}): SkillsData => {
  const { value: occupationPreferencesData, loading: occupationPreferencesLoading } = useFetchData({
    useQueryData: useOccupationPreferencesData,
    questionIdForLocalStorage: QUESTION_IDS.roleSelect,
    skip,
  });

  const shouldSkipSkillsQuery = occupationPreferencesLoading || !occupationPreferencesData?.length || skip;

  const { data: rankedRolesData, loading: rankedRolesLoading } = useQuery<
    GetSkillsFromRankedRolesQuery,
    GetSkillsFromRankedRolesQueryVariables
  >(GetSkillsFromRankedRoles, {
    skip: shouldSkipSkillsQuery,
    variables: {
      ids: occupationPreferencesData?.map((role) => role.id) || [],
      limit: 1,
    },
    context: {
      clientName: 'gatewayGql',
    },
    errorPolicy: 'all',
  });

  let mappedRoleSkills: Skill[] = [];

  const topRankedRole = rankedRolesData?.PathwaySkillCareerRoles?.queryRecommendedRolesByIds?.entities?.elements?.[0];

  if (topRankedRole?.__typename === 'Skill_CareerRole') {
    mappedRoleSkills = topRankedRole.skills || [];
  }

  if (!occupationPreferencesLoading && !rankedRolesLoading && !mappedRoleSkills.length) {
    return {
      allSkills: getPopularSkills(),
      loading: false,
      type: 'POPULAR_SKILLS',
    };
  }

  return {
    allSkills: mappedRoleSkills.slice(0, limit),
    loading: occupationPreferencesLoading || rankedRolesLoading,
    type: 'ROLE_SKILLS',
  };
};

export default useGetAllSkills;
